import type {
	LiveSessionItem,
	SessionItem,
} from "@memorang/types/src/session/new-session";
import {
	buildProethicaModule,
	buildProethicaTest,
} from "./build-proethica-module";
import type { ProethicaModuleSummary } from "./proethica";
import {
	type NewFormatModuleItem,
	findModuleOverview,
	isProethicaTestItem,
} from "./proethica-adapter";

// Live ProEthica module sessions are created SUMMATIVE/PRACTICE_TEST against the
// demo-content project.
export const proethicaLiveSessionConfig = {
	projectId: 26,
	organizationId: 24,
	sessionType: "SUMMATIVE",
	sessionSubType: "PRACTICE_TEST",
} as const;

// Module 1 is the session created when no specific module is selected.
const PROETHICA_DEFAULT_COLLECTION_ID = 783;

// Module identity IS the collectionId, so create-session takes it straight.
export const getProethicaLiveCreateParams = (moduleId?: string) => {
	const collectionId =
		(moduleId ? Number(moduleId) : undefined) ??
		PROETHICA_DEFAULT_COLLECTION_ID;
	return { ...proethicaLiveSessionConfig, collectionId };
};

const isLiveProethicaItem = (item: unknown): boolean =>
	Boolean((item as { metadata?: { ui_type?: string } })?.metadata?.ui_type);

const deriveModuleLabel = (
	items: NewFormatModuleItem[],
	response: { examName?: string },
): string => {
	const fromName = response.examName?.match(/Module\s+(\d+)/i)?.[1];
	const moduleNumber = fromName ?? findModuleOverview(items)?.moduleNumber;
	return `Module ${moduleNumber ?? 1}`;
};

/**
 * Single convergence point for ProEthica session data: live create-session
 * responses are converted to legacy `SessionItem`s so components render
 * unchanged; any already-legacy response (other demo exams, the live API once
 * wired) passes through untouched.
 */
export const normalizeProethicaSessionResponse = <
	T extends {
		sessionItems?: SessionItem[] | LiveSessionItem[];
		examName?: string;
	},
>(
	response: T,
): Omit<T, "sessionItems"> & {
	sessionItems: SessionItem[];
	moduleSummary?: ProethicaModuleSummary;
} => {
	const items = response.sessionItems ?? [];
	if (!(items as unknown[]).some(isLiveProethicaItem)) {
		return { ...response, sessionItems: items as SessionItem[] };
	}

	const raw = items as unknown as NewFormatModuleItem[];
	if (raw.some(isProethicaTestItem)) {
		const { sessionItems } = buildProethicaTest(raw);
		return { ...response, sessionItems };
	}

	// The backend sends the MODULE_ENDING summary as a live item; buildProethicaModule
	// strips it from the session items, so surface it here for the report's summary card.
	const { sessionItems, summary } = buildProethicaModule({
		raw,
		moduleLabel: deriveModuleLabel(raw, response),
	});
	return { ...response, sessionItems, moduleSummary: summary };
};
